import React, { useState } from 'react';
import { X, Lock } from 'lucide-react';
import { Platform, EventPriority, EventStatus, Event } from '../../../types/calendar';
import { useCalendarStore } from '../../../stores/calendarStore';
import RecurrenceSelector from './event/RecurrenceSelector';
import ReminderSelector from './event/ReminderSelector';
import ContentSelector from './event/ContentSelector';
import { useNavigate } from 'react-router-dom';
import { formatTime, parseTime } from '../../../utils/date';

interface EventModalProps {
  date: Date;
  event?: Event;
  hasPremiumAccess: boolean;
  onClose: () => void;
}

const PREDEFINED_COLORS = [
  { label: 'Indigo', value: '#4F46E5' },
  { label: 'Sky', value: '#0EA5E9' },
  { label: 'Emerald', value: '#10B981' },
  { label: 'Amber', value: '#F59E0B' },
  { label: 'Rose', value: '#F43F5E' },
  { label: 'Violet', value: '#8B5CF6' },
  { label: 'Slate', value: '#64748B' },
];

export default function EventModal({ date, event, hasPremiumAccess, onClose }: EventModalProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(event?.title || '');
  const [platform, setPlatform] = useState<Platform>(event?.platform || 'instagram');
  const [priority, setPriority] = useState<EventPriority>(event?.priority || 'medium');
  const [status, setStatus] = useState<EventStatus>(event?.status || 'draft');
  const [selectedDate, setSelectedDate] = useState(event ? new Date(event.start) : date);
  const [startTime, setStartTime] = useState(
    event ? formatTime(new Date(event.start)) : '09:00'
  );
  const [endTime, setEndTime] = useState(
    event ? formatTime(new Date(event.end)) : '10:00'
  );
  const [color, setColor] = useState(event?.color || PREDEFINED_COLORS[0].value);
  const [isCustomColor, setIsCustomColor] = useState(false);
  const [recurrence, setRecurrence] = useState(event?.recurrence || null);
  const [reminders, setReminders] = useState<string[]>(event?.reminders || []);
  const [contentType, setContentType] = useState<'idea' | 'plan' | null>(
    event?.contentIdeaId ? 'idea' : event?.contentPlanId ? 'plan' : null
  );
  const [selectedContentId, setSelectedContentId] = useState<string>(
    event?.contentIdeaId || event?.contentPlanId || ''
  );

  const { addEvent, updateEvent } = useCalendarStore();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const start = new Date(selectedDate);
    const startParts = startTime.split(':');
    start.setHours(parseInt(startParts[0]), parseInt(startParts[1]));

    const end = new Date(selectedDate);
    const endParts = endTime.split(':');
    end.setHours(parseInt(endParts[0]), parseInt(endParts[1]));

    const eventData = {
      title,
      start,
      end,
      platform,
      priority: hasPremiumAccess ? priority : 'medium' as const,
      status: hasPremiumAccess ? status : 'draft' as const,
      color,
      recurrence: hasPremiumAccess ? recurrence : null,
      reminders: hasPremiumAccess ? reminders : [],
      contentPlanId: hasPremiumAccess && contentType === 'plan' ? selectedContentId : undefined,
      contentIdeaId: hasPremiumAccess && contentType === 'idea' ? selectedContentId : undefined
    };

    if (event) {
      updateEvent(event.id, eventData);
    } else {
      addEvent(eventData);
    }

    onClose();
  };

  const handleContentSelect = (type: 'idea' | 'plan', id: string) => {
    if (!hasPremiumAccess) {
      navigate('/dashboard/precos');
      return;
    }
    
    if (type === contentType && id === selectedContentId) {
      setContentType(null);
      setSelectedContentId('');
    } else {
      setContentType(type);
      setSelectedContentId(id);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">
            {event ? 'Editar Evento' : 'Novo Evento'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              required
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data
              </label>
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Início
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fim
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Platform, Priority, Status */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plataforma
              </label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as Platform)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>
            
            {/* Priority - Premium Feature */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prioridade
                {!hasPremiumAccess && (
                  <Lock className="inline h-4 w-4 ml-1 text-gray-400" />
                )}
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as EventPriority)}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent ${
                  !hasPremiumAccess ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={!hasPremiumAccess}
                onClick={() => {
                  if (!hasPremiumAccess) {
                    navigate('/dashboard/precos');
                  }
                }}
              >
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
              </select>
            </div>
            
            {/* Status - Premium Feature */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
                {!hasPremiumAccess && (
                  <Lock className="inline h-4 w-4 ml-1 text-gray-400" />
                )}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as EventStatus)}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent ${
                  !hasPremiumAccess ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={!hasPremiumAccess}
                onClick={() => {
                  if (!hasPremiumAccess) {
                    navigate('/dashboard/precos');
                  }
                }}
              >
                <option value="draft">Rascunho</option>
                <option value="scheduled">Agendado</option>
                <option value="published">Publicado</option>
              </select>
            </div>
          </div>

          {/* Content Selector - Premium Feature */}
          {hasPremiumAccess ? (
            <ContentSelector
              selectedContentId={selectedContentId}
              selectedContentType={contentType}
              onSelect={handleContentSelect}
            />
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <Lock className="h-5 w-5 text-gray-400" />
                <div>
                  <h3 className="font-medium text-gray-900">Vincular Conteúdo</h3>
                  <p className="text-sm text-gray-600">
                    Atualize para o plano Premium ou Pro para vincular ideias e planos ao seu calendário
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/precos')}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-2"
                  >
                    Ver planos disponíveis →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recurrence - Premium Feature */}
          {hasPremiumAccess ? (
            <RecurrenceSelector
              value={recurrence}
              onChange={setRecurrence}
            />
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <Lock className="h-5 w-5 text-gray-400" />
                <div>
                  <h3 className="font-medium text-gray-900">Eventos Recorrentes</h3>
                  <p className="text-sm text-gray-600">
                    Atualize para o plano Premium ou Pro para criar eventos recorrentes
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/precos')}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-2"
                  >
                    Ver planos disponíveis →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reminders - Premium Feature */}
          {hasPremiumAccess ? (
            <ReminderSelector
              value={reminders}
              onChange={setReminders}
            />
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <Lock className="h-5 w-5 text-gray-400" />
                <div>
                  <h3 className="font-medium text-gray-900">Lembretes</h3>
                  <p className="text-sm text-gray-600">
                    Atualize para o plano Premium ou Pro para adicionar lembretes aos seus eventos
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/precos')}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-2"
                  >
                    Ver planos disponíveis →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cor
            </label>
            <div className="space-y-4">
              {/* Predefined colors */}
              <div className="flex flex-wrap gap-3">
                {PREDEFINED_COLORS.map((presetColor) => (
                  <button
                    key={presetColor.value}
                    type="button"
                    onClick={() => {
                      setColor(presetColor.value);
                      setIsCustomColor(false);
                    }}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      color === presetColor.value && !isCustomColor
                        ? 'border-gray-900 scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: presetColor.value }}
                    title={presetColor.label}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setIsCustomColor(true)}
                  className={`w-8 h-8 rounded-lg border-2 border-dashed transition-all ${
                    isCustomColor ? 'border-gray-900 scale-110' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  title="Cor personalizada"
                >
                  <span className="sr-only">Escolher cor personalizada</span>
                </button>
              </div>

              {/* Custom color input */}
              {isCustomColor && (
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-full h-10 p-1 rounded-lg cursor-pointer"
                />
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              {event ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}